import React from "react";
import {
  Send,
  FileText,
  ShieldAlert,
  Clock,
  Shuffle,
  CircleDollarSign,
  UserX,
  Network,
  ShieldCheck,
  ClipboardCheck,
  Route,
  BadgeDollarSign,
  CheckCircle2,
} from "lucide-react";

const XS = [70, 202, 333, 465, 597, 728, 860];
const NODE = 46;
const Y1 = 120;
const Y2 = 320;
const DUR = "7s";
const frac = (x) => +((x - 70) / 790).toFixed(3);

const STYLE = {
  neutral: { fill: "#FFFFFF", stroke: "#E2E8F0", icon: "#64748B" },
  friction: { fill: "#FFFBEB", stroke: "#F59E0B", icon: "#D97706" },
  bad: { fill: "#FEF2F2", stroke: "#F87171", icon: "#DC2626" },
  start: { fill: "#FFFFFF", stroke: "#CBD5E1", icon: "#475569" },
  hub: { fill: "#2563EB", stroke: "#2563EB", icon: "#FFFFFF" },
  done: { fill: "#FFFFFF", stroke: "#5EEAD4", icon: "#0D9488" },
  success: { fill: "#ECFDF5", stroke: "#34D399", icon: "#059669" },
};

const ROW1 = [
  { label: ["Provider", "Clicks Send"], Icon: Send, state: "neutral" },
  { label: ["Enters Fragmented", "System"], Icon: FileText, state: "neutral" },
  { label: ["Insurance", "Issues"], Icon: ShieldAlert, state: "friction" },
  { label: ["Prior Auth", "Delays"], Icon: Clock, state: "friction" },
  { label: ["Routing", "Confusion"], Icon: Shuffle, state: "friction" },
  { label: ["Affordability", "Barriers"], Icon: CircleDollarSign, state: "friction" },
  { label: ["Patient Delayed", "/ Abandons"], Icon: UserX, state: "bad" },
];

const ROW2 = [
  { label: ["Provider", "Clicks Send"], Icon: Send, state: "start" },
  { label: ["RxAnywhere", "Infrastructure"], Icon: Network, state: "hub" },
  { label: ["Coverage", "Review"], Icon: ShieldCheck, state: "done" },
  { label: ["Prior Auth", "Support"], Icon: ClipboardCheck, state: "done" },
  { label: ["Routing", "Coordination"], Icon: Route, state: "done" },
  { label: ["Affordability", "Support"], Icon: BadgeDollarSign, state: "done" },
  { label: ["Patient Receives", "Medication"], Icon: CheckCircle2, state: "success" },
];

const Connectors = ({ y, teal }) =>
  XS.slice(0, -1).map((x, i) => (
    <line
      key={i}
      x1={x + NODE / 2}
      y1={y}
      x2={XS[i + 1] - NODE / 2}
      y2={y}
      stroke={teal ? "#FCD34D" : "#E2E8F0"}
      strokeWidth={teal ? 2.5 : 2}
      strokeLinecap="round"
      markerEnd={teal ? "url(#arrTeal)" : "url(#arrGray)"}
    />
  ));

const NodeCard = ({ node, x, y }) => {
  const s = STYLE[node.state];
  const { Icon, label, state } = node;
  const f = frac(x);
  const a = Math.max(0.001, +(f - 0.03).toFixed(3));
  return (
    <g>
      <rect
        x={x - NODE / 2}
        y={y - NODE / 2}
        width={NODE}
        height={NODE}
        rx="12"
        fill={s.fill}
        stroke={s.stroke}
        strokeWidth="1.75"
        filter="url(#cardShadow)"
      />
      {state === "hub" && (
        <rect x={x - NODE / 2} y={y - NODE / 2} width={NODE} height={NODE} rx="12" fill="#2563EB" opacity="0.5" filter="url(#softGlow)">
          <animate attributeName="opacity" values="0.25;0.55;0.25" dur="2.6s" repeatCount="indefinite" />
        </rect>
      )}
      <Icon x={x - 11} y={y - 11} width={22} height={22} color={s.icon} strokeWidth={1.75} />

      {/* Friction warning badge (top row) */}
      {state === "friction" && (
        <g transform={`translate(${x + 17}, ${y - 17})`}>
          <circle r="8.5" fill="#F59E0B">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <path d="M0 -4 L4 3.5 L-4 3.5 Z" fill="none" stroke="#FFFFFF" strokeWidth="1.4" strokeLinejoin="round" />
          <rect x="-0.7" y="-1.5" width="1.4" height="3" rx="0.7" fill="#FFFFFF" />
          <circle cx="0" cy="2.4" r="0.8" fill="#FFFFFF" />
        </g>
      )}

      {/* Green completion check (RxAnywhere row: done nodes) */}
      {state === "done" && (
        <g transform={`translate(${x + 17}, ${y - 17})`}>
          <g>
            <animateTransform attributeName="transform" type="scale" values="0.3;0.3;1;1;0.3" keyTimes={`0;${a};${f};0.96;1`} dur={DUR} repeatCount="indefinite" />
            <circle r="8.5" fill="#22C55E">
              <animate attributeName="opacity" values="0;0;1;1;0" keyTimes={`0;${a};${f};0.96;1`} dur={DUR} repeatCount="indefinite" />
            </circle>
            <path d="M-3.5 0 L-1 2.5 L4 -3" fill="none" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <animate attributeName="opacity" values="0;0;1;1;0" keyTimes={`0;${a};${f};0.96;1`} dur={DUR} repeatCount="indefinite" />
            </path>
          </g>
        </g>
      )}

      {/* Success node persistent check */}
      {state === "success" && (
        <g transform={`translate(${x + 17}, ${y - 17})`}>
          <circle r="8.5" fill="#22C55E">
            <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.9;0.96;1" dur={DUR} repeatCount="indefinite" />
          </circle>
          <path d="M-3.5 0 L-1 2.5 L4 -3" fill="none" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.9;0.96;1" dur={DUR} repeatCount="indefinite" />
          </path>
        </g>
      )}

      <text x={x} y={y + 36} textAnchor="middle" fontSize="10" fontWeight="600" fill="#334155" style={{ fontFamily: "Inter, sans-serif" }}>
        {label[0]}
      </text>
      <text x={x} y={y + 48} textAnchor="middle" fontSize="10" fontWeight="600" fill="#334155" style={{ fontFamily: "Inter, sans-serif" }}>
        {label[1]}
      </text>
    </g>
  );
};

const Pulse = ({ path, keyPoints, keyTimes }) => (
  <g>
    <circle r="10" fill="url(#haloBlue)">
      <animateMotion dur={DUR} repeatCount="indefinite" path={path} calcMode="linear" keyPoints={keyPoints} keyTimes={keyTimes} />
    </circle>
    <circle r="4.5" fill="#FFD600" filter="url(#softGlow)">
      <animateMotion dur={DUR} repeatCount="indefinite" path={path} calcMode="linear" keyPoints={keyPoints} keyTimes={keyTimes} />
    </circle>
    <circle r="4.5" fill="#FFD600">
      <animateMotion dur={DUR} repeatCount="indefinite" path={path} calcMode="linear" keyPoints={keyPoints} keyTimes={keyTimes} />
    </circle>
  </g>
);

export const PrescriptionJourney = () => {
  const path1 = `M${XS[0]},${Y1} L${XS[6]},${Y1}`;
  const path2 = `M${XS[0]},${Y2} L${XS[6]},${Y2}`;

  return (
    <div className="rx-journey w-full max-w-[1180px] mx-auto" data-testid="prescription-journey">
      <svg viewBox="0 0 920 470" className="w-full h-auto" style={{ overflow: "visible" }}>
        <title>The Prescription Journey — Traditional vs With RxAnywhere</title>
        <defs>
          <filter id="cardShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="#0F172A" floodOpacity="0.06" />
          </filter>
          <filter id="softGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          <radialGradient id="haloBlue">
            <stop offset="0%" stopColor="#FFD600" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#FFD600" stopOpacity="0" />
          </radialGradient>
          <marker id="arrGray" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L6,3 L0,6 Z" fill="#CBD5E1" />
          </marker>
          <marker id="arrTeal" markerWidth="7" markerHeight="7" refX="5.5" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L6,3 L0,6 Z" fill="#FBBF24" />
          </marker>
        </defs>

        {/* Row 1 — Traditional Process */}
        <rect x="58" y="44" width="188" height="28" rx="14" fill="#0A0A0A" />
        <circle cx="76" cy="58" r="4" fill="#F59E0B" />
        <text x="88" y="62" fontSize="14" fontWeight="700" fill="#FFFFFF" style={{ fontFamily: "Inter, sans-serif" }}>
          Traditional Process
        </text>
        <Connectors y={Y1} teal={false} />
        {ROW1.map((n, i) => (
          <NodeCard key={`r1-${i}`} node={n} x={XS[i]} y={Y1} />
        ))}
        <Pulse
          path={path1}
          keyPoints="0;0.166;0.333;0.333;0.5;0.5;0.666;0.666;0.833;0.833;1;1"
          keyTimes="0;0.10;0.22;0.32;0.42;0.52;0.60;0.70;0.78;0.88;0.97;1"
        />

        {/* Row 2 — With RxAnywhere */}
        <rect x="58" y="244" width="164" height="28" rx="14" fill="#FFD600" />
        <circle cx="76" cy="258" r="4" fill="#2563EB" />
        <text x="88" y="262" fontSize="14" fontWeight="700" fill="#0A0A0A" style={{ fontFamily: "Inter, sans-serif" }}>
          With RxAnywhere
        </text>
        <Connectors y={Y2} teal={true} />
        {ROW2.map((n, i) => (
          <NodeCard key={`r2-${i}`} node={n} x={XS[i]} y={Y2} />
        ))}
        <Pulse path={path2} />

        {/* End caption */}
        <g>
          <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.88;0.95;1" dur={DUR} repeatCount="indefinite" />
          <g transform="translate(370, 432)">
            <circle r="9" fill="#22C55E" />
            <path d="M-3.6 0 L-1 2.6 L4 -3.2" fill="none" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <text x="390" y="437" fontSize="15" fontWeight="700" fill="#16A34A" style={{ fontFamily: "Inter, sans-serif" }}>
            Medication Access Complete
          </text>
        </g>
      </svg>
    </div>
  );
};

export default PrescriptionJourney;
